<template>
    <Card
        icon="fas fa-shopping-cart"
        bg-color="tw:bg-[var(--sitecolor)]"
        title-color="white"
        title-classes="tw:border-b tw:border-gray-200"
        :title="'Venta online'"
        padding="0"
    >
        <div class="tw:p-6 tw:space-y-4">
            <div class="tw:space-y-3">
                <ToggleSwitch
                    :value="localData.venta_online_cerrada"
                    :disabled="!!localData.preventa_configurada"
                    @input="val => set(localData, 'venta_online_cerrada', val)"
                    variant="primary"
                    id="customCheckbox23"
                >
                    {{ 'Cerrar venta online' }}
                    <Tooltip
                        v-if="localData.preventa_configurada"
                        :text="'Mientras la preventa esté activa, la venta online permanece cerrada automáticamente. Desmarca la preventa para poder modificarlo.'"
                        variant="warning"
                        class="tw:normal-case"
                    />
                </ToggleSwitch>

                <div v-if="localData.venta_online_cerrada" class="tw:ml-6" ref="ventaCerradaSection">
                    <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                        {{ 'Fecha de apertura de venta' }}
                    </label>
                    <BaseDateTimeInput
                        :label="'Fecha de apertura'"
                        name="fecha_apertura_de_venta"
                        :value="localData.cuenta_atras"
                        :min="minFechaApertura"
                        :max="maxFechaApertura"
                        :disabled="fechasEventoFaltan"
                        :rules="'fechaAperturaVenta'"
                        @input="val => set(localData, 'cuenta_atras', val)"
                    />
                    <span
                        v-if="fechasEventoFaltan"
                        data-cy="aviso-fechas-evento-faltan"
                        class="tw:text-xs tw:text-red-600 tw:font-medium tw:block tw:mt-1"
                    >
                        {{ 'Para fijar la fecha de apertura de venta es necesario indicar la fecha de inicio y la fecha de fin del evento.' }}
                    </span>

                </div>

                <ToggleSwitch
                    :value="localData.venta_online_cerrada_definitiva"
                    @input="val => localData.venta_online_cerrada_definitiva = val"
                    variant="primary"
                    id="customCheckbox24"
                >
                    {{ 'Cerrar la venta online definitivamente' }}
                </ToggleSwitch>

                <ToggleSwitch
                    :value="localData.entradas_agotadas_online"
                    @input="val => localData.entradas_agotadas_online = val"
                    variant="primary"
                    id="entradas_agotadas_online"
                >
                    {{ 'Marcar como entradas agotadas' }}
                    <Tooltip
                        :text="'Al marcar este check saldrán entradas agotadas en la venta online pero se seguirá pudiendo comprar en taquilla.'"
                        variant="success"
                        class="tw:normal-case"
                    />
                </ToggleSwitch>
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:space-y-5">
                <!-- Entradas nominativas: obligatorias mientras haya un
                     Evento SGA/LFP seleccionado (rule solicitarDatosRequerido
                     en useEventForm.js). Al activar Experiencia el evento se
                     limpia y las nominativas vuelven a ser opcionales. -->
                <ValidationProvider
                    name="solicitar_datos"
                    :rules="solicitarDatosRule"
                    :value="localData.solicitar_datos"
                    v-slot="{ errors }"
                    mode="eager"
                >
                    <ToggleSwitch
                        :value="localData.solicitar_datos"
                        @input="val => localData.solicitar_datos = val"
                        :disabled="!!(localData.sga_id || localData.lfp_id) && !localData.experiencia"
                        variant="primary"
                        id="customCheckbox25"
                    >
                        {{ 'Entradas nominativas' }}
                        <span v-if="errors[0]" class="tw:block tw:text-xs tw:text-red-600">{{ errors[0] }}</span>
                    </ToggleSwitch>
                </ValidationProvider>

                <div v-if="localData.solicitar_datos" class="tw:ml-6 tw:mt-2 tw:flex tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-start" ref="solicitarDatosSection">
                    <div class="tw:flex-1">
                        <div class="tw:flex tw:items-center tw:gap-1 tw:mb-1">
                            <span class="tw:text-sm tw:font-medium tw:text-gray-700">
                                {{ 'Tiempo límite' }}
                            </span>
                            <Tooltip
                                :text="'Tiempo límite para que el usuario pueda cambiar los datos de su entrada desde el perfil antes de la fecha de inicio del evento.'"
                                variant="success"
                                class="tw:normal-case"
                            />
                        </div>
                        <BaseMultiselect
                            id="limite_tiempo_cambiar_nombre_entrada"
                            :label="'Tiempo'"
                            name="limite_tiempo"
                            :value="localData.limite_tiempo_cambiar_nombre_entrada"
                            :options="limitesTiempo"
                            label-key="nombre"
                            value-key="id"
                            :multiple="false"
                            @input="val => set(localData, 'limite_tiempo_cambiar_nombre_entrada', val)"
                        />
                    </div>

                    <div class="tw:flex-1">
                        <div class="tw:flex tw:items-center tw:gap-1 tw:mb-1">
                            <span class="tw:text-sm tw:font-medium tw:text-gray-700">
                                {{ 'Coste de cambio' }}
                            </span>
                            <Tooltip
                                :text="'Importe que se le cobrará al usuario al querer cambiar los datos de la entrada desde su perfil, si no se pone ningún valor o sepone 0, el cambio de datos no tendrá coste adicional.'"
                                variant="success"
                                class="tw:normal-case"
                            />
                        </div>
                        <BaseInputNumber
                            id="coste-cambio-nombre"
                            name="coste cambio nombre"
                            :label="'Coste'"
                            data-cy="coste-cambio-nombre"
                            :value="localData.coste_cambio_nombre"
                            decimals-only
                            disallow-negatives
                            decimal-rule="decimal_opcional"
                            :step="0.01"
                            @input="val => set(localData, 'coste_cambio_nombre', val)"
                        />
                    </div>
                </div>
            </div>

            <template v-if="!localData.experiencia">
                <div class="tw:border-t tw:border-gray-100"></div>
                <ToggleSwitch
                    :value="localData.solo_abonados"
                    @input="val => localData.solo_abonados = val"
                    variant="primary"
                    id="customCheckbox5"
                    data-cy="solo-abonados"
                >
                    {{ 'Solo abonados' }}
                    <Tooltip
                        :text="'Solo podrán realizar la compra los Abonados, en caso de no ser abonado el usuario no abonado no podrá acceder a la compra. Esta opción desactivada implica que se abre la venta a usuarios no abonados.'"
                        variant="success"
                        class="tw:normal-case"
                    />
                </ToggleSwitch>
            </template>

            <div class="tw:border-t tw:border-gray-100"></div>

            <ToggleSwitch
                :value="localData.compra_unica"
                @input="val => localData.compra_unica = val"
                variant="primary"
                id="checkbox_compra_unica"
            >
                {{ 'Compra única' }}
                <Tooltip
                    :text="'Cuando hay cola virtual solo dejara comprar una vez, luego le llevara a la cola virtual otra vez.'"
                    variant="success"
                    class="tw:normal-case"
                />
            </ToggleSwitch>
        </div>
    </Card>
</template>

<script setup>
    import { inject, set, ref, computed } from 'vue';
    import { useScrollOnToggle } from '@/composables/common/useScrollOnToggle';
    import Card from '@/modules/admin/ui/Card.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import BaseDateTimeInput from '@/modules/admin/ui/BaseDateTimeInput.vue';
    import Tooltip from '@/modules/admin/ui/Tooltip.vue';

    const localData = inject('eventoFormData');

    const props = defineProps({
        limitesTiempo: { type: Array, default: () => [] },
        manager: { type: Object, default: () => ({}) },
        // When true, activating "Entradas nominativas" does NOT scroll to its
        // section. FormEvento sets it for one tick when it auto-activates
        // nominativas after selecting an SGA event (programmatic activation:
        // the page already jumped to the pickers; scrolling to Ventas too
        // would be disorienting). User clicks keep the scroll.
        suppressNominativasScroll: { type: Boolean, default: false }
    });

    // Min/Max para el input "Fecha de apertura de venta" (cuenta_atrás).
    // Min siempre es "ahora" (computed al montar). Max es end_event_date si
    // está definida. Si start_event_date o end_event_date no están definidas
    // → el usuario ve un aviso (estilo error) indicando que debe rellenarlas
    // primero y el input queda deshabilitado. La validación dura de que la
    // apertura no supere la fecha de fin la aporta la custom rule
    // `fechaAperturaVenta` (definida en useEventForm.js), porque el atributo
    // `max` nativo del datetime-local no impide teclear una fecha mayor a mano.
    const minFechaApertura = computed(() => new Date().toISOString().slice(0, 16));
    const maxFechaApertura = computed(() => localData.end_event_date || undefined);
    const fechasEventoFaltan = computed(() =>
        !localData.start_event_date || !localData.end_event_date
    );

    const ventaCerradaSection = ref(null);
    const solicitarDatosSection = ref(null);

    // Dynamic rule for the nominativas ValidationProvider: only enforce
    // `solicitarDatosRequerido` while an SGA/LFP event is selected. When no
    // event is selected (or Experiencia cleared it) the toggle is free.
    const solicitarDatosRule = computed(() =>
        (localData.sga_id || localData.lfp_id) ? 'solicitarDatosRequerido' : ''
    );

    useScrollOnToggle(() => localData.venta_online_cerrada, ventaCerradaSection);
    useScrollOnToggle(() => localData.solicitar_datos, solicitarDatosSection, {
        // Programmatic activation (SGA event selection) suppresses the scroll;
        // the user clicking the toggle still scrolls (enabled() re-evaluated
        // at fire time).
        enabled: () => !props.suppressNominativasScroll
    });
</script>
